<script setup>
import { ref, computed, getCurrentInstance } from "vue";
import api from "../api/api.js";
import { flashMessage } from "../functions.js";

// ── Customer / Account Profile ────────────────────────────────────────────────
const profile = ref({
  customerId:    "774513",
  accountNumber: "862232",
  accountType:   "Clinic",
  creditLimit:   150000.00,
  ytdPurchases:  98420.00,
  totalOrders:   31,
  firstName:     "John",
  lastName:      "Smith",
  title:         "Procurement Officer",
  dateOfBirth:   "1979-09-05",
  idType:        "Driver's License",
  idNumber:      "MN-5541892",
  email:         "johnsmith@mnetteam.com",
  phone:         "+1 (312) 752-8183",
  address:       "ABC Clinic, 1234 Medical Center Dr, Rochester, MN 55902",
  affiliatedClinics:   "ABC Clinic – Rochester, MN",
  affiliatedHospitals: "Mayo Clinic Health System",
  medicalOrgs:         "AAOS, ACS",
  territory:     "Midwest US",
  salesRep:      "Jack Thompson",
  profileSince:  "2022-08-10",
  lastUpdated:   "2026-06-15",
});

const account = ref({
  caseId:          "CS-20260614-7742",
  status:          "Active",
  company:         "ABC Clinic",
  location:        "Rochester, MN, USA",
  contactReason:   "Stock / Back-order Inquiry",
  priority:        "Normal",
  firstName:       "John",
  lastName:        "Smith",
  email:           "johnsmith@mnetteam.com",
  phone:           "+1 (312) 752-8183",
  address:         "1234 Medical Center Dr, Rochester, MN 55902",
  paymentTerms:    "Paid",
  outstandingBalance: 0.00,
  creditAvailable:    150000.00,
  lastOrderDate:   "2026-06-15",
  lastOrderAmount: 14820.00,
  pendingItems: [
    { item: "Order #862232 — BioBrace® Extra-Articular Ligament Augmentation Kit (x30)", amount: 12600.00 },
    { item: "Order #862232 — CoLink® Bone Graft Harvester (x1)",                         amount: 2220.00  },
    { item: "Shipping & Handling — Standard",                                             amount: 0.00     },
  ],
  specialNotes: ["Allopatch back-order — email alert requested when stock resumes. Notify: johnsmith@mnetteam.com"],
});

const remarks = ref([
  { id: 1, date: "2026-06-15", type: "Inquiry",   agent: "J. Martin", note: "Customer escalated from chat — inquiring about Allopatch back-order availability. Advised still out of stock. Set up email alert for johnsmith@mnetteam.com when supply resumes to ABC Clinic." },
  { id: 2, date: "2026-06-15", type: "Order",     agent: "System",      note: "Order #862232 delivered June 15, 2026 to ABC Clinic, Rochester, MN. Items: BioBrace® Extra-Articular Ligament Augmentation Kit (x30), CoLink® Bone Graft Harvester (x1). Payment Status: PAID." },
  { id: 3, date: "2026-05-20", type: "Inquiry",   agent: "D. Cruz",     note: "Customer asked about AirSeal® iFS insufflator compatibility with OR setup. Connected with clinical support. Resolved same day." },
  { id: 4, date: "2026-04-10", type: "Note",      agent: "J. Martin", note: "Quarterly sales check-in. Customer interested in PlumeSafe® X5™ demo. Scheduled for April 25 at Rochester facility." },
  { id: 5, date: "2026-02-28", type: "Discount",  agent: "J. Martin", note: "Gold-tier volume discount 5% applied to Q1 order. Total savings: $490. Customer satisfied with service." },
  { id: 6, date: "2025-12-01", type: "Complaint", agent: "A. Torres",   note: "Reported delayed shipment on BioBrace order (#861004). Resolved — shipment expedited, delivered Dec 4. Apology discount applied to next order." },
]);

const products = ref([
  { id: 1, icon: "🦴", name: "BioBrace® Ligament Augmentation Kit", category: "Sports Medicine", sku: "BB-EALA",  price: "$420",   offered: true  },
  { id: 2, icon: "🔩", name: "CoLink® Bone Graft Harvester",        category: "Spine",           sku: "CL-BGH1",  price: "$2,220", offered: true  },
  { id: 3, icon: "💨", name: "AirSeal® iFS Insufflator",            category: "Insufflation",    sku: "AS-IFS3",  price: "$1,840", offered: false },
  { id: 4, icon: "💨", name: "PlumeSafe® X5™ Smoke Evacuator",     category: "Smoke Evacuation", sku: "PS-X5",    price: "$3,100", offered: false },
  { id: 5, icon: "⚡", name: "LigaSure™ Vessel Sealing System",    category: "Energy",           sku: "LS-1500T", price: "$2,980", offered: false },
  { id: 6, icon: "🔬", name: "ERBE VIO 3 Electrosurgical Unit",    category: "Electrosurgery",   sku: "VIO3-200D",price: "$4,200", offered: false },
  { id: 7, icon: "🧪", name: "Allopatch HD® Soft Tissue Patch",    category: "Biologics",        sku: "AP-HD",    price: "$380",   offered: false },
]);

const showIdNumber       = ref(false);
const newRemarkNote      = ref("");
const newRemarkType      = ref("Inquiry");
const remarkTypes        = ["Inquiry", "Complaint", "Order", "Discount", "Note"];

const fmt = (v) => "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2 });

const creditUsedPct = computed(() =>
  Math.round((account.value.outstandingBalance / profile.value.creditLimit) * 100)
);

const remarkTypeColor = (type) => ({
  Inquiry:   "#1d6fa5",
  Complaint: "#b91c1c",
  Order:     "#065f46",
  Discount:  "#92400e",
  Note:      "#374151",
}[type] || "#374151");

function saveRemark() {
  if (!newRemarkNote.value.trim()) return;
  remarks.value.unshift({
    id:    Date.now(),
    date:  new Date().toISOString().slice(0, 10),
    type:  newRemarkType.value,
    agent: "Current Agent",
    note:  newRemarkNote.value.trim(),
  });
  newRemarkNote.value = "";
}

// ── OTP ──────────────────────────────────────────────────────────────────────
const OTP_PHONE             = "+13127528183";
const otpInput              = ref("");
const verificationAttempted = ref(false);
const isValid               = ref(false);
const logocheck             = require("@/assets/check.png");
const logoclose             = require("@/assets/close.png");
const { proxy }             = getCurrentInstance();

function flash(message, color = "#4CAF50") {
  flashMessage(proxy.$flashMessage, color, "#ffffff", message);
}

function openCRM() {
  const imagePath = require(`@/assets/core_salesforce.png`);
  window.open(imagePath, "_blank", "width=1340,height=800");
}

async function sendSMS() {
  try {
    const response = await api.post("/v2/SendOTP", {
      Company:      "5028",
      cellPhone:    OTP_PHONE,
      AuthToken:    "3301",
      memberNumber: "774513",
    }, {
      headers: { token: "2y10D08eZgJLx0C9rGvQKfwFBuCPvqDdmdowpnczUKwYWU3B0VoVt5BiC" }
    });
    flash(response.data.Response || "OTP sent");
  } catch {
    flash("Failed to send OTP", "#FF0000");
  }
}

async function verifyOTP() {
  try {
    const response = await api.post("/v2/CheckOTP", {
      Company:   "5028",
      cellPhone: OTP_PHONE,
      OTP:       otpInput.value,
      AuthToken: "3301",
    }, {
      headers: { token: "2y10D08eZgJLx0C9rGvQKfwFBuCPvqDdmdowpnczUKwYWU3B0VoVt5BiC" }
    });
    verificationAttempted.value = true;
    isValid.value = response.data.Response === "OTP Validated";
    flash(response.data.Response, isValid.value ? "#4CAF50" : "#FF0000");
  } catch {
    flash("Failed to verify OTP", "#FF0000");
  }
}
</script>

<template>
  <div class="cm-page">
    <div class="cm-card">

      <!-- ── Card header ──────────────────────────────────────────────────── -->
      <div class="cm-topbar">
        <h2 class="cm-screen-id">Customer Information</h2>
        <div class="cm-topbar-right">
          <button class="cm-goto-btn" @click="openCRM">Go to CRM</button>
          <span class="cm-screen-code">C01</span>
        </div>
      </div>

      <!-- ── Subheader strip ───────────────────────────────────────────────── -->
      <div class="cm-subheader">
        <div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Case ID:</span>
            <span class="cm-sub-val">{{ account.caseId }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Name:</span>
            <span class="cm-sub-val cm-sub-val--name">{{ account.firstName }} {{ account.lastName }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Email:</span>
            <span class="cm-sub-val">{{ account.email }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Phone:</span>
            <span class="cm-sub-val">{{ account.phone }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Address:</span>
            <span class="cm-sub-val">{{ account.address }}</span>
          </div>
        </div>
        <div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Status:</span>
            <span class="cm-sub-val cm-sub-val--status">{{ account.status }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Company:</span>
            <span class="cm-sub-val">{{ account.company }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Reason:</span>
            <span class="cm-sub-val">{{ account.contactReason }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Territory:</span>
            <span class="cm-sub-val">{{ profile.territory }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Sales Rep:</span>
            <span class="cm-sub-val">{{ profile.salesRep }}</span>
          </div>
        </div>
        <div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Payment:</span>
            <span class="cm-sub-val" style="font-weight:700;">{{ account.paymentTerms }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Credit Limit:</span>
            <span class="cm-sub-val">{{ fmt(profile.creditLimit) }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Outstanding:</span>
            <span class="cm-sub-val" style="color:#dc2626; font-weight:700;">{{ fmt(account.outstandingBalance) }}</span>
          </div>
          <div class="cm-sub-row">
            <span class="cm-sub-lbl">Available:</span>
            <span class="cm-sub-val" style="color:#16a34a; font-weight:600;">{{ fmt(account.creditAvailable) }}</span>
          </div>
          <div class="cm-progress-wrap" style="margin-top:6px;">
            <div class="cm-progress-bar">
              <div class="cm-progress-fill" :style="{ width: creditUsedPct + '%' }"></div>
            </div>
            <span class="cm-progress-lbl">{{ creditUsedPct }}% credit used</span>
          </div>
        </div>
      </div>

      <!-- ── 3-column body ──────────────────────────────────────────────────── -->
      <div class="cm-centerdiv">

        <!-- ═══ COL 1: Identity + 2FA ════════════════════════════════════════ -->
        <div class="cm-col">

          <!-- Search -->
          <h4>Search for</h4>
          <div class="cm-search-group">
            <input class="cm-search-input" type="text" placeholder="Order number, PO, or case ID" />
            <input class="cm-search-input" type="text" placeholder="Email Address" />
          </div>
          <div style="margin: 8px 0 18px;">
            <button class="cm-submit-btn">Find</button>
            <button class="cm-cancel-btn">Cancel</button>
          </div>

          <!-- Customer Identity -->
          <h4>Customer Identity</h4>

          <!-- Account ID strip -->
          <div class="cm-profile-ids">
            <div class="cm-profile-id-item">
              <span class="cm-sub-lbl">CRM Customer #</span>
              <span class="cm-profile-id-val">{{ profile.customerId }}</span>
            </div>
            <div class="cm-profile-id-item">
              <span class="cm-sub-lbl">Order / Conf. #</span>
              <span class="cm-profile-id-val">{{ profile.accountNumber }}</span>
            </div>
            <div class="cm-profile-id-item">
              <span class="cm-sub-lbl">Orders (YTD)</span>
              <span class="cm-profile-id-val">{{ profile.totalOrders }}</span>
            </div>
            <div class="cm-profile-id-item">
              <span class="cm-sub-lbl">YTD Purchases</span>
              <span class="cm-profile-id-val">{{ fmt(profile.ytdPurchases) }}</span>
            </div>
          </div>

          <table class="cm-info-table" style="margin-top:8px;">
            <tbody>
              <tr>
                <td class="cm-td-lbl">Title</td>
                <td>{{ profile.title }}</td>
              </tr>
              <tr>
                <td class="cm-td-lbl">Account Type</td>
                <td>{{ profile.accountType }}</td>
              </tr>
              <tr>
                <td class="cm-td-lbl">ID Type</td>
                <td>{{ profile.idType }}</td>
              </tr>
              <tr>
                <td class="cm-td-lbl">ID Number</td>
                <td>
                  {{ showIdNumber ? profile.idNumber : "••••••••••" }}
                  <button class="cm-pill-btn" @click="showIdNumber = !showIdNumber">
                    {{ showIdNumber ? "Hide" : "Show" }}
                  </button>
                </td>
              </tr>
              <tr><td class="cm-td-lbl">Date of Birth</td><td>{{ profile.dateOfBirth }}</td></tr>
              <tr><td class="cm-td-lbl">Aff. Clinics</td><td>{{ profile.affiliatedClinics }}</td></tr>
              <tr><td class="cm-td-lbl">Aff. Hospitals</td><td>{{ profile.affiliatedHospitals }}</td></tr>
              <tr><td class="cm-td-lbl">Medical Orgs</td><td>{{ profile.medicalOrgs }}</td></tr>
              <tr><td class="cm-td-lbl">Last Order</td><td>{{ account.lastOrderDate }} — {{ fmt(account.lastOrderAmount) }}</td></tr>
              <tr><td class="cm-td-lbl">Customer Since</td><td>{{ profile.profileSince }}</td></tr>
              <tr><td class="cm-td-lbl">Last Updated</td><td>{{ profile.lastUpdated }}</td></tr>
            </tbody>
          </table>

          <!-- 2FA -->
          <fieldset style="margin-top:20px; border:1px solid #e5e7eb; border-radius:6px; padding:8px 12px; display:inline-flex; flex-direction:column; gap:8px;">
            <legend style="font-weight:600; font-size:13px; color:#374151; padding:0 6px;">2FA</legend>
            <div style="display:flex; align-items:center; gap:6px;">
              <input class="cm-search-input" :value="account.phone" disabled style="background:#f3f4f6; color:#374151; margin:0; flex:1;" />
              <button class="cm-submit-btn" @click="sendSMS" style="margin:0; white-space:nowrap;">Send SMS</button>
            </div>
            <div style="display:flex; align-items:center; gap:6px;">
              <input class="cm-search-input" v-model="otpInput" placeholder="OTP" style="margin:0; width:80px; flex:none;" />
              <img v-if="verificationAttempted && isValid"  :src="logocheck" alt="valid"   style="width:18px;height:18px;flex-shrink:0;" />
              <img v-if="verificationAttempted && !isValid" :src="logoclose" alt="invalid" style="width:18px;height:18px;flex-shrink:0;" />
              <button class="cm-submit-btn" @click="verifyOTP" style="margin:0;">Validate</button>
            </div>
          </fieldset>

          <!-- Special Notes -->
          <h4 style="margin-top:20px;">Special Notes</h4>
          <textarea class="cm-textarea" rows="3" readonly
            :value="account.specialNotes.join('\n')"></textarea>

        </div>

        <!-- ═══ COL 2: Remarks / Case History ════════════════════════════════ -->
        <div class="cm-col">

          <h4>Remarks &amp; Case History</h4>
          <div style="display:flex; gap:8px; margin-bottom:10px; align-items:flex-start;">
            <div class="cm-remark-type-wrap">
              <select class="cm-remark-type-select" v-model="newRemarkType">
                <option v-for="t in remarkTypes" :key="t">{{ t }}</option>
              </select>
              <span class="cm-remark-type-chevron">▾</span>
            </div>
            <textarea class="cm-textarea" v-model="newRemarkNote"
              placeholder="Add a remark..." rows="2"
              style="flex:1; margin:0;"></textarea>
          </div>
          <div style="margin-bottom:14px;">
            <button class="cm-submit-btn" @click="saveRemark">Save</button>
            <button class="cm-cancel-btn" @click="newRemarkNote=''">Cancel</button>
          </div>

          <table class="cm-data-table">
            <thead>
              <tr><th>Type</th><th>Date</th><th>Agent</th><th>Note</th></tr>
            </thead>
            <tbody>
              <tr v-for="r in remarks" :key="r.id">
                <td style="white-space:nowrap;">
                  <span class="cm-type-dot" :style="{ background: remarkTypeColor(r.type) }"></span>
                  {{ r.type }}
                </td>
                <td style="white-space:nowrap; color:#555;">{{ r.date }}</td>
                <td style="white-space:nowrap; color:#555;">{{ r.agent }}</td>
                <td>{{ r.note }}</td>
              </tr>
            </tbody>
          </table>

        </div>

        <!-- ═══ COL 3: Product Recommendations + Pending Orders ══════════════ -->
        <div class="cm-col">

          <!-- Product Recommendations -->
          <h4>Product Recommendations</h4>
          <p class="cm-hint">Click a row to mark as presented to customer.</p>
          <table class="cm-data-table">
            <thead>
              <tr><th></th><th>Product</th><th>Category</th><th>Price</th><th>Status</th></tr>
            </thead>
            <tbody>
              <tr v-for="p in products" :key="p.id"
                class="cm-product-row" :class="{ offered: p.offered }"
                @click="p.offered = !p.offered">
                <td>{{ p.icon }}</td>
                <td>
                  <div style="font-weight:600; font-size:13px;">{{ p.name }}</div>
                  <div style="font-size:11px; color:#888;">{{ p.sku }}</div>
                </td>
                <td style="white-space:nowrap; font-size:12px; color:#555;">{{ p.category }}</td>
                <td style="white-space:nowrap;">{{ p.price }}</td>
                <td>
                  <span v-if="p.offered" class="cm-offered-badge">Presented ✓</span>
                  <span v-else class="cm-pending-badge">—</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pending Orders -->
          <h4 style="margin-top:20px;">Open / Pending Orders</h4>
          <table class="cm-data-table">
            <thead>
              <tr><th>Item / PO</th><th>Amount</th></tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in account.pendingItems" :key="i">
                <td>{{ item.item }}</td>
                <td style="text-align:right; white-space:nowrap;">{{ fmt(item.amount) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total Outstanding</strong></td>
                <td style="text-align:right; white-space:nowrap;">
                  <strong>{{ fmt(account.pendingItems.reduce((s, i) => s + i.amount, 0)) }}</strong>
                </td>
              </tr>
            </tfoot>
          </table>

        </div>

      </div>
    </div><!-- /cm-card -->
  </div>
</template>

<style scoped>
.cm-page {
  font-family: "Open Sans", "Segoe UI", sans-serif;
  font-size: 15px;
  background: #e8e8e8;
  min-height: 100vh;
  padding: 20px;
  color: #222;
  box-sizing: border-box;
}

.cm-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.12);
  overflow: hidden;
}

/* ── Header ─────────────────────────────────────────────────────────────────── */
.cm-topbar {
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
}
.cm-screen-id   { font-size: 18px; font-weight: 600; color: #222; margin: 0; }
.cm-screen-code { font-size: 18px; font-weight: 600; color: #888; }
.cm-topbar-right { display: flex; align-items: center; gap: 14px; }
.cm-goto-btn {
  background: #0e5071;
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.02em;
}
.cm-goto-btn:hover { background: #0c4160; }

/* ── Subheader ───────────────────────────────────────────────────────────────── */
.cm-subheader {
  border-bottom: 1px solid #e8e8e8;
  padding: 10px 20px;
  display: flex;
  gap: 60px;
}
.cm-sub-row { display: flex; gap: 6px; padding: 1px 0; font-size: 14px; }
.cm-sub-lbl { color: #555; min-width: 90px; }
.cm-sub-val { color: #222; }
.cm-sub-val--name   { font-weight: 600; }
.cm-sub-val--status { font-weight: 700; color: #0e5071; }

/* ── 3-column layout ─────────────────────────────────────────────────────────── */
.cm-centerdiv {
  display: flex;
  gap: 0;
  padding: 0;
  align-items: flex-start;
}
.cm-col {
  flex: 1;
  padding: 16px 20px;
  min-width: 0;
  border-right: 1px solid #e8e8e8;
}
.cm-col:last-child { border-right: none; }
.cm-col h4 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 10px;
  color: #222;
}

/* ── Account ID strip ────────────────────────────────────────────────────────── */
.cm-profile-ids {
  display: flex;
  gap: 0;
  background: #f5f7f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}
.cm-profile-id-item {
  flex: 1;
  padding: 7px 10px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.cm-profile-id-item:last-child { border-right: none; }
.cm-profile-id-val {
  font-size: 13px;
  font-weight: 600;
  color: #0e5071;
  font-family: monospace;
}

/* ── Search ──────────────────────────────────────────────────────────────────── */
.cm-search-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 0; }
.cm-search-input {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 5px 8px;
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
}
.cm-search-input:focus { outline: none; border-color: #0e5071; }

/* ── Info table ──────────────────────────────────────────────────────────────── */
.cm-info-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.cm-info-table td { padding: 4px 6px; vertical-align: top; }
.cm-td-lbl { color: #555; width: 110px; white-space: nowrap; }

/* ── Data table ──────────────────────────────────────────────────────────────── */
.cm-data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.cm-data-table thead tr { background: #0e5071; }
.cm-data-table th {
  padding: 7px 10px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #fff !important;
  white-space: nowrap;
}
.cm-data-table tbody tr:nth-child(odd)  { background: #fff; }
.cm-data-table tbody tr:nth-child(even) { background: #f5f7f9; }
.cm-data-table tbody tr { border-bottom: 1px solid #e8e8e8; }
.cm-data-table tbody tr:last-child { border-bottom: none; }
.cm-data-table td { padding: 6px 10px; vertical-align: top; color: #333; }
.cm-data-table tfoot td { background: #fff; border-top: 2px solid #ddd; padding-top: 8px; }

/* ── Progress bar ────────────────────────────────────────────────────────────── */
.cm-progress-wrap { display: flex; align-items: center; gap: 8px; margin: 8px 0 0; }
.cm-progress-bar  { flex: 1; height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.cm-progress-fill { height: 100%; background: #0e5071; border-radius: 4px; }
.cm-progress-lbl  { font-size: 12px; color: #555; white-space: nowrap; }

/* ── Textarea ────────────────────────────────────────────────────────────────── */
.cm-textarea {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 6px 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  color: #222;
  box-sizing: border-box;
}

/* ── Remark type dropdown ────────────────────────────────────────────────────── */
.cm-remark-type-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.cm-remark-type-select {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid #b0b0b0;
  border-radius: 4px;
  padding: 5px 28px 5px 10px;
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  background: #fff;
  color: #454a4d;
  cursor: pointer;
  min-width: 100px;
}
.cm-remark-type-select:focus { outline: none; border-color: #0e5071; }
.cm-remark-type-chevron {
  position: absolute;
  right: 8px;
  pointer-events: none;
  font-size: 11px;
  color: #888;
}

/* ── Buttons ─────────────────────────────────────────────────────────────────── */
.cm-submit-btn, .cm-cancel-btn, .cm-pill-btn {
  border: none;
  border-radius: 20px;
  padding: 5px 18px;
  font-size: 13px;
  cursor: pointer;
  margin-right: 6px;
}
.cm-submit-btn { background: #0e5071; color: #fff; }
.cm-submit-btn:hover { background: #0c4160; }
.cm-cancel-btn { background: #b0b0b0; color: #fff; }
.cm-cancel-btn:hover { background: #999; }
.cm-pill-btn   { background: #e0e0e0; color: #333; font-size: 11px; padding: 2px 10px; margin-left: 6px; }

/* ── Type dot ────────────────────────────────────────────────────────────────── */
.cm-type-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}

/* ── Hint ────────────────────────────────────────────────────────────────────── */
.cm-hint { font-size: 12px; color: #888; margin: -6px 0 8px; }

/* ── Product row ─────────────────────────────────────────────────────────────── */
.cm-product-row { cursor: pointer; }
.cm-product-row:hover { background: #fff5f5; }
.cm-product-row.offered { background: #f0fdf4; }
.cm-offered-badge { background: #d1fae5; color: #065f46; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.cm-pending-badge { color: #aaa; font-size: 13px; }
</style>
